import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Button — scaffold shadcn (new-york) reskinado para tokens NDS.
 *
 * Decisões que vêm do playbook e não devem ser "ajustadas no olho":
 *  - raio `rounded-button` = 6px (regra viva; a produção do sustenta-ai-app
 *    usa 12px e isso está registrado como deriva conhecida);
 *  - só uma variante `primary` por tela — as demais são outline/ghost/danger;
 *  - estado de carregando mantém o rótulo visível e adiciona `Loader2`; nunca
 *    troca o texto por um spinner mudo;
 *  - alvo de toque mínimo 44px vem do padding, não de um tamanho visual maior.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-body font-bold transition-colors duration-fast ease-standard disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-on-primary shadow-rest hover:bg-primary-hover active:bg-primary-active',
        outline:
          'border border-border bg-bg-surface text-on-surface shadow-rest hover:border-border-strong hover:bg-bg-subtle',
        ghost: 'text-on-surface hover:bg-bg-subtle',
        link: 'h-auto px-0 text-primary underline-offset-4 hover:underline',
        danger: 'bg-danger text-on-primary shadow-rest hover:brightness-95 active:brightness-90',
      },
      size: {
        md: 'h-10 px-4 py-2',
        sm: 'h-8 px-3 text-label',
        icon: 'size-10',
      },
    },
    defaultVariants: { variant: 'outline', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Mostra progresso inline e desabilita, preservando o rótulo. */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {/* Com `asChild` o Radix exige UM único filho: o elemento passado (um
            `<Link>`, por exemplo) é adotado como o próprio botão. Envolvê-lo
            junto do spinner quebraria o Slot — e `loading` não se aplica a um
            botão que na verdade é navegação. */}
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
